import React, { useState, useEffect, useRef } from 'react';
import * as Tone from 'tone';

const FXPanel = ({ 
  deck, 
  isEnabled = true,
  audioNode // The audio source to apply FX to
}) => {
  // FX States
  const [filter, setFilter] = useState({ enabled: false, type: 'lowpass', frequency: 1000 });
  const [reverb, setReverb] = useState({ enabled: false, wet: 0.3, decay: 2 });
  const [delay, setDelay] = useState({ enabled: false, wet: 0.3, time: 0.25, feedback: 0.3 });
  const [echo, setEcho] = useState({ enabled: false, wet: 0.3, delayTime: 0.4, feedback: 0.5 });
  
  // FX Node refs
  const filterRef = useRef(null);
  const reverbRef = useRef(null);
  const delayRef = useRef(null);
  const echoRef = useRef(null);
  
  // Initialize FX nodes
  useEffect(() => {
    // Create FX nodes
    filterRef.current = new Tone.Filter(filter.frequency, filter.type);
    reverbRef.current = new Tone.Reverb({ decay: reverb.decay, wet: reverb.wet });
    delayRef.current = new Tone.FeedbackDelay(delay.time, delay.feedback);
    delayRef.current.wet.value = delay.wet;
    echoRef.current = new Tone.PingPongDelay(echo.delayTime, echo.feedback);
    echoRef.current.wet.value = echo.wet;
    
    return () => {
      filterRef.current?.dispose();
      reverbRef.current?.dispose();
      delayRef.current?.dispose();
      echoRef.current?.dispose();
    };
  }, []);
  
  // Update filter
  useEffect(() => {
    if (filterRef.current) {
      filterRef.current.frequency.value = filter.frequency;
      filterRef.current.type = filter.type;
    }
  }, [filter.frequency, filter.type]);
  
  // Update reverb
  useEffect(() => {
    if (reverbRef.current) {
      reverbRef.current.wet.value = reverb.enabled ? reverb.wet : 0;
    }
  }, [reverb.enabled, reverb.wet]);
  
  // Update delay
  useEffect(() => {
    if (delayRef.current) {
      delayRef.current.wet.value = delay.enabled ? delay.wet : 0;
      delayRef.current.delayTime.value = delay.time;
      delayRef.current.feedback.value = delay.feedback;
    }
  }, [delay.enabled, delay.wet, delay.time, delay.feedback]);
  
  // Update echo
  useEffect(() => {
    if (echoRef.current) {
      echoRef.current.wet.value = echo.enabled ? echo.wet : 0;
      echoRef.current.delayTime.value = echo.delayTime;
      echoRef.current.feedback.value = echo.feedback;
    }
  }, [echo.enabled, echo.wet, echo.delayTime, echo.feedback]);
  
  const deckColor = deck === 'A' ? '#00d4ff' : '#ff6b35';
  
  return (
    <div className={`fx-panel fx-panel-${deck.toLowerCase()}`} data-testid={`fx-panel-${deck.toLowerCase()}`}>
      <div className="fx-header">
        <span className="fx-title">FX</span>
        <span className="fx-deck" style={{ color: deckColor }}>Deck {deck}</span>
      </div>
      
      <div className="fx-controls">
        {/* Filter */}
        <div className={`fx-unit ${filter.enabled ? 'active' : ''}`}>
          <button 
            className="fx-toggle"
            onClick={() => setFilter(f => ({ ...f, enabled: !f.enabled }))}
            disabled={!isEnabled}
          >
            <span className="fx-name">FILTER</span>
            <span className={`fx-indicator ${filter.enabled ? 'on' : ''}`} />
          </button>
          {filter.enabled && (
            <div className="fx-params">
              <select 
                value={filter.type}
                onChange={(e) => setFilter(f => ({ ...f, type: e.target.value }))}
                className="fx-select"
              >
                <option value="lowpass">LP</option>
                <option value="highpass">HP</option>
                <option value="bandpass">BP</option>
              </select>
              <input
                type="range"
                min="50"
                max="15000"
                value={filter.frequency}
                onChange={(e) => setFilter(f => ({ ...f, frequency: parseInt(e.target.value) }))}
                className="fx-knob"
              />
              <span className="fx-value">{Math.round(filter.frequency)}Hz</span>
            </div>
          )}
        </div>
        
        {/* Reverb */}
        <div className={`fx-unit ${reverb.enabled ? 'active' : ''}`}>
          <button 
            className="fx-toggle"
            onClick={() => setReverb(r => ({ ...r, enabled: !r.enabled }))}
            disabled={!isEnabled}
          >
            <span className="fx-name">REVERB</span>
            <span className={`fx-indicator ${reverb.enabled ? 'on' : ''}`} />
          </button>
          {reverb.enabled && (
            <div className="fx-params">
              <label>Wet</label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={reverb.wet}
                onChange={(e) => setReverb(r => ({ ...r, wet: parseFloat(e.target.value) }))}
                className="fx-knob"
              />
              <span className="fx-value">{Math.round(reverb.wet * 100)}%</span>
            </div>
          )}
        </div>
        
        {/* Delay */}
        <div className={`fx-unit ${delay.enabled ? 'active' : ''}`}>
          <button 
            className="fx-toggle"
            onClick={() => setDelay(d => ({ ...d, enabled: !d.enabled }))}
            disabled={!isEnabled}
          >
            <span className="fx-name">DELAY</span>
            <span className={`fx-indicator ${delay.enabled ? 'on' : ''}`} />
          </button>
          {delay.enabled && (
            <div className="fx-params">
              <label>Time</label>
              <input
                type="range"
                min="0.05"
                max="1"
                step="0.05"
                value={delay.time}
                onChange={(e) => setDelay(d => ({ ...d, time: parseFloat(e.target.value) }))}
                className="fx-knob"
              />
              <span className="fx-value">{delay.time.toFixed(2)}s</span>
            </div>
          )}
        </div>
        
        {/* Echo */}
        <div className={`fx-unit ${echo.enabled ? 'active' : ''}`}>
          <button 
            className="fx-toggle"
            onClick={() => setEcho(e => ({ ...e, enabled: !e.enabled }))}
            disabled={!isEnabled}
          >
            <span className="fx-name">ECHO</span>
            <span className={`fx-indicator ${echo.enabled ? 'on' : ''}`} />
          </button>
          {echo.enabled && (
            <div className="fx-params">
              <label>Feedback</label>
              <input
                type="range"
                min="0.1"
                max="0.9"
                step="0.05"
                value={echo.feedback}
                onChange={(e) => setEcho(ec => ({ ...ec, feedback: parseFloat(e.target.value) }))}
                className="fx-knob"
              />
              <span className="fx-value">{Math.round(echo.feedback * 100)}%</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FXPanel;
